import { useEffect, useRef, type Dispatch } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { isAdvancePercentMode, resolveAdvanceAmount } from '@pomoshnik/shared';
import { genUuid } from '@/lib/uuid';
import { SectionCard } from '../../anketa/components/SectionCard';
import { SectionHeader } from '../../anketa/components/SectionHeader';
import { ADVANCE_ROW_ID, REMAINDER_ROW_ID, type ConditionField } from '../conditionRows';
import { DEFAULT_CONDITIONS } from '../conditionRows';
import type { QuoteAction } from '../quoteReducer';
import type { ConditionRow, QuoteState } from '../types';

/**
 * Block 12 PR-D-A Phase D4 Step 6 — раздел «Условия».
 *
 * 5 default-строк + custom additions. Прототип
 * `docs/prototypes/screen-2.12.10-cp.html` lines 203-272.
 *
 * **Auto-computed remainder:** строка с `id='remainder'` рендерится с
 * `readOnly` input'ом; value пересчитывается на каждый render через
 * `computeRemainder(state.costWithMargin, advance.value)` — НЕ хранится в
 * state. Защищает от рассинхронизации advance/cost/remainder.
 *
 * **Default vs custom layout:**
 *  - default row: label (above) + input (below) + unit suffix
 *  - custom row: label input + value input + trash (inline flex)
 *
 * **Numeric vs text input:** default rows с unit ∈ {'рабочих дней','₸','мес'} →
 * type='number' inputMode='numeric'; rows с unit=undefined (special-conditions
 * + custom) → type='text'.
 *
 * Init pattern (mirror Phase D3 Composition): useEffect → CONDITION_INIT_DEFAULTS
 * если conditions.length === 0. Reducer idempotent.
 */
export interface SectionConditionsProps {
  state: QuoteState;
  dispatch: Dispatch<QuoteAction>;
}

const DEFAULT_ROW_IDS: ReadonlySet<string> = new Set(DEFAULT_CONDITIONS.map((r) => r.id));

export function SectionConditions({ state, dispatch }: SectionConditionsProps) {
  const rows = state.conditions;
  const cost = state.costWithMargin;

  // One-time mount init. После первого render — initRef.current=true,
  // даже если пользователь удалит все строки (delete-to-zero не должно
  // re-init'ить defaults, иначе нельзя стартануть с пустой формой).
  const initRef = useRef(false);
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    if (rows.length === 0) dispatch({ type: 'CONDITION_INIT_DEFAULTS' });
  }, [rows.length, dispatch]);

  // PR-D2 (ADR-0040) — эффективная сумма аванса: из ставки «Аванс, %» когда
  // задана (Вариант A), иначе из ручного значения строки-условия. Остаток
  // считается от эффективной суммы (не от raw value строки).
  const advanceRow = rows.find((r) => r.id === ADVANCE_ROW_ID);
  const advancePercent = state.advancePercent ?? null;
  const percentMode = isAdvancePercentMode(advancePercent);
  const manualAdvance = Number(advanceRow?.value ?? '');
  const effectiveAdvance = resolveAdvanceAmount(
    cost ?? 0,
    advancePercent,
    Number.isFinite(manualAdvance) ? manualAdvance : 0,
  );
  const computedRemainder = Math.max(0, (cost ?? 0) - effectiveAdvance);

  const handleUpdate = (id: string, field: ConditionField, value: string) => {
    dispatch({ type: 'CONDITION_UPDATE', id, field, value });
  };
  const handlePercentChange = (raw: string) => {
    const trimmed = raw.trim();
    dispatch({ type: 'SET_ADVANCE_PERCENT', value: trimmed === '' ? null : Number(trimmed) });
  };
  const handleAdd = () => {
    dispatch({ type: 'CONDITION_ADD_CUSTOM', id: genUuid() });
  };
  const handleDelete = (id: string) => {
    dispatch({ type: 'CONDITION_DELETE', id });
  };

  return (
    <SectionCard testId="section-conditions">
      <SectionHeader>Условия</SectionHeader>
      <ul role="list" className="flex flex-col gap-3" data-testid="conditions-list">
        {rows.map((row) => {
          // PR-D2 (ADR-0040) — строка аванса рендерит ПЕРЕД суммой поле
          // «Аванс, %»; при заданном % поле суммы read-only (производная сумма).
          if (row.id === ADVANCE_ROW_ID) {
            return (
              <AdvanceConditionRow
                key={row.id}
                row={row}
                advancePercent={advancePercent}
                percentMode={percentMode}
                effectiveAdvance={effectiveAdvance}
                onUpdate={handleUpdate}
                onPercentChange={handlePercentChange}
              />
            );
          }
          return DEFAULT_ROW_IDS.has(row.id) ? (
            <DefaultConditionRow
              key={row.id}
              row={row}
              isRemainder={row.id === REMAINDER_ROW_ID}
              remainderValue={computedRemainder}
              onUpdate={handleUpdate}
            />
          ) : (
            <CustomConditionRow
              key={row.id}
              row={row}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
            />
          );
        })}
      </ul>

      <button
        type="button"
        onClick={handleAdd}
        aria-label="Добавить условие"
        data-testid="conditions-add-row"
        className="w-full min-h-12 mt-3 rounded-xl border-2 border-dashed border-accent bg-card text-ink text-base font-semibold flex items-center justify-center gap-2 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-80"
      >
        <Plus size={18} aria-hidden="true" />
        Добавить строку
      </button>
    </SectionCard>
  );
}

interface DefaultConditionRowProps {
  row: ConditionRow;
  isRemainder: boolean;
  remainderValue: number;
  onUpdate: (id: string, field: ConditionField, value: string) => void;
}

const INPUT_CLASS =
  'w-full min-h-12 px-3 rounded-xl border-[1.5px] border-accent bg-card text-base text-ink focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none';

function DefaultConditionRow({
  row,
  isRemainder,
  remainderValue,
  onUpdate,
}: DefaultConditionRowProps) {
  const isNumber = row.unit !== undefined;
  const labelWithUnit = row.unit ? `${row.label} (${row.unit})` : row.label;
  const fieldId = `quote-cond-${row.id}`;

  if (isRemainder) {
    return (
      <li>
        <label htmlFor={fieldId} className="block text-sm font-semibold text-ink/80 mb-1">
          Остаток
        </label>
        <input
          id={fieldId}
          type="number"
          inputMode="numeric"
          readOnly
          aria-readonly="true"
          value={remainderValue}
          aria-label="Остаток (рассчитывается автоматически)"
          data-testid="condition-remainder-input"
          className={`${INPUT_CLASS} bg-canvas text-muted cursor-not-allowed`}
        />
        {remainderValue >= 1000 || remainderValue === 0 ? (
          <p
            className="mt-1 text-xs text-muted"
            data-testid="condition-remainder-formatted"
            aria-live="polite"
          >
            {remainderValue.toLocaleString('ru-RU')} ₸
          </p>
        ) : null}
      </li>
    );
  }

  return (
    <li>
      <label htmlFor={fieldId} className="block text-sm font-semibold text-ink/80 mb-1">
        {labelWithUnit}
      </label>
      <input
        id={fieldId}
        type={isNumber ? 'number' : 'text'}
        inputMode={isNumber ? 'numeric' : undefined}
        min={isNumber ? 0 : undefined}
        value={row.value}
        onChange={(e) => onUpdate(row.id, 'value', e.target.value)}
        aria-label={labelWithUnit}
        data-testid={`condition-${row.id}-input`}
        className={INPUT_CLASS}
      />
    </li>
  );
}

interface AdvanceConditionRowProps {
  row: ConditionRow;
  advancePercent: number | null;
  percentMode: boolean;
  effectiveAdvance: number;
  onUpdate: (id: string, field: ConditionField, value: string) => void;
  onPercentChange: (raw: string) => void;
}

/**
 * PR-D2 (ADR-0040) — строка «Размер аванса» с полем «Аванс, %» НАД суммой.
 *
 * Вариант A: % задан валидным числом (0–100) → поле суммы read-only, value =
 * `effectiveAdvance` (= round(Стоимость × %/100), пересчитывается при изменении
 * Стоимости). Чтобы ввести сумму вручную — очистить «Аванс, %» (percentMode=false →
 * поле суммы редактируемое, value = `row.value`, как до PR-D2).
 */
function AdvanceConditionRow({
  row,
  advancePercent,
  percentMode,
  effectiveAdvance,
  onUpdate,
  onPercentChange,
}: AdvanceConditionRowProps) {
  const percentFieldId = `quote-cond-advance-percent`;
  const amountFieldId = `quote-cond-${row.id}`;
  return (
    <li className="flex flex-col gap-3">
      <div>
        <label htmlFor={percentFieldId} className="block text-sm font-semibold text-ink/80 mb-1">
          Аванс, %
        </label>
        <input
          id={percentFieldId}
          type="number"
          inputMode="numeric"
          min={0}
          max={100}
          value={advancePercent ?? ''}
          onChange={(e) => onPercentChange(e.target.value)}
          placeholder="напр. 60"
          aria-label="Аванс в процентах от стоимости"
          data-testid="condition-advance-percent-input"
          className={INPUT_CLASS}
        />
      </div>
      <div>
        <label htmlFor={amountFieldId} className="block text-sm font-semibold text-ink/80 mb-1">
          {row.unit ? `${row.label} (${row.unit})` : row.label}
        </label>
        <input
          id={amountFieldId}
          type="number"
          inputMode="numeric"
          min={0}
          readOnly={percentMode}
          aria-readonly={percentMode || undefined}
          value={percentMode ? effectiveAdvance : row.value}
          onChange={(e) => onUpdate(row.id, 'value', e.target.value)}
          aria-label={
            percentMode
              ? 'Размер аванса (рассчитывается из процента)'
              : `${row.label} (${row.unit})`
          }
          data-testid={`condition-${row.id}-input`}
          className={
            percentMode ? `${INPUT_CLASS} bg-canvas text-muted cursor-not-allowed` : INPUT_CLASS
          }
        />
        {percentMode && effectiveAdvance > 0 ? (
          <p className="mt-1 text-xs text-muted" aria-live="polite">
            {effectiveAdvance.toLocaleString('ru-RU')} ₸
          </p>
        ) : null}
      </div>
    </li>
  );
}

interface CustomConditionRowProps {
  row: ConditionRow;
  onUpdate: (id: string, field: ConditionField, value: string) => void;
  onDelete: (id: string) => void;
}

function CustomConditionRow({ row, onUpdate, onDelete }: CustomConditionRowProps) {
  const rowName = row.label.trim() || 'без названия';
  return (
    <li className="flex items-end gap-1.5">
      <div className="flex-1 min-w-0">
        <label className="block text-xs font-semibold text-muted mb-1">Название</label>
        <input
          type="text"
          value={row.label}
          onChange={(e) => onUpdate(row.id, 'label', e.target.value)}
          placeholder="Например: Сертификат"
          aria-label={`Название условия: ${rowName}`}
          className={INPUT_CLASS}
        />
      </div>
      <div className="flex-1 min-w-0">
        <label className="block text-xs font-semibold text-muted mb-1">Значение</label>
        <input
          type="text"
          value={row.value}
          onChange={(e) => onUpdate(row.id, 'value', e.target.value)}
          placeholder="—"
          aria-label={`Значение условия: ${rowName}`}
          className={INPUT_CLASS}
        />
      </div>
      <button
        type="button"
        onClick={() => onDelete(row.id)}
        aria-label={`Удалить условие ${rowName}`}
        className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-md text-destructive focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-70"
      >
        <Trash2 size={18} aria-hidden="true" />
      </button>
    </li>
  );
}
